import { createAddressTitle, ParsedAsset, type Enums } from '@aikoa/common'
import { parseAsset } from '@aikoa/common'
import { supabase } from '~/lib/supabase'

const ASSETS_MANAGEMENT_PAGE_SIZE = 20

const ASSETS_SELECT_WITH_FEEDBACK = `*,
  users_assets!inner(
    id,
    relation_type,
    data,
    user_id
  ),
  address:asset_addresses(*),
  prices:asset_prices!inner(*)
`

export async function getAllUserAssets({
  userId,
  relationTypes,
}: {
  userId: string
  relationTypes?: Enums<'user_asset_relation'>[]
}) {
  if (!userId) {
    console.error('User not found')
    return []
  }

  const query = supabase
    .from('assets')
    .select(
      `*,
      users_assets!inner(
        id, 
        relation_type,
        data,
        user_id
      ),
      address:asset_addresses(*),
      prices:asset_prices!inner(*)
    `
    )
    .is('deleted_at', null)
    .eq('users_assets.user_id', userId)
    .is('asset_prices.deleted_at', null)
    .order('created_at', { ascending: false, referencedTable: 'asset_prices' })
    .order('created_at', { ascending: false, referencedTable: 'users_assets' })

  if (relationTypes) {
    query.in('users_assets.relation_type', relationTypes)
  }

  const { data, error } = await query.order('updated_at', { ascending: false })

  if (error) {
    console.error(error)
    return []
  }

  return data.map((item) => parseAsset(item))
}

export async function getAssetsByIds({
  userId,
  assetIds,
  relationType,
}: {
  userId: string
  assetIds: string[]
  relationType?: Enums<'user_asset_relation'>
}) {
  if (!userId || !assetIds || assetIds.length === 0) {
    return []
  }

  const indexById = new Map<string, number>()

  for (let assetIndex = 0; assetIndex < assetIds.length; assetIndex++) {
    const id = assetIds[assetIndex]
    indexById.set(id, assetIndex)
  }

  const query = supabase
    .from('assets')
    .select(
      `*,
      users_assets!inner(
        id, 
        relation_type,
        data,
        user_id
      ),
      address:asset_addresses(*),
      prices:asset_prices(*)
    `
    )
    .eq('users_assets.user_id', userId)
    .in('id', assetIds)
    .is('asset_prices.deleted_at', null)
    .order('created_at', { ascending: false, referencedTable: 'asset_prices' })
    .order('created_at', { ascending: false, referencedTable: 'users_assets' })

  if (relationType) {
    query.eq('users_assets.relation_type', relationType)
  }

  const { data, error } = await query

  if (error || !data) {
    if (error) {
      console.error(error)
    }

    return []
  }

  const parsed = data.map((item) => parseAsset(item))

  parsed.sort((left, right) => {
    const a = indexById.get(left.id) ?? Number.MAX_SAFE_INTEGER
    const b = indexById.get(right.id) ?? Number.MAX_SAFE_INTEGER
    return a - b
  })

  return parsed
}

export type AssetsManagementSortBy = 'created_at' | 'updated_at'

type GetUserAssetsPageInput = {
  userId: string
  offset: number
  pageSize?: number
  sortBy?: AssetsManagementSortBy
  sortDirection?: 'asc' | 'desc'
}

type UserAssetsPageResult = {
  assets: ParsedAsset[]
  nextOffset: number | null
}

export async function getUserAssetsPage({
  userId,
  offset,
  pageSize = ASSETS_MANAGEMENT_PAGE_SIZE,
  sortBy = 'created_at',
  sortDirection = 'desc',
}: GetUserAssetsPageInput): Promise<UserAssetsPageResult> {
  if (!userId) {
    return { assets: [], nextOffset: null }
  }

  const rangeStart = Math.max(offset, 0)
  const rangeEnd = rangeStart + pageSize - 1

  const query = supabase
    .from('assets')
    .select(ASSETS_SELECT_WITH_FEEDBACK)
    .is('deleted_at', null)
    .eq('users_assets.user_id', userId)
    .is('asset_prices.deleted_at', null)
    .range(rangeStart, rangeEnd)

  if (sortBy === 'created_at') {
    query.order('created_at', {
      ascending: sortDirection === 'asc',
    })
  } else {
    query.order('updated_at', { ascending: sortDirection === 'asc' })
  }

  query.order('name', { ascending: true })

  const { data, error } = await query

  if (error) {
    console.error(error)
    return { assets: [], nextOffset: null }
  }

  const rawItems = data ?? []
  const assets = rawItems.map((rawItem) => parseAsset(rawItem))
  const hasMore = rawItems.length === pageSize

  return {
    assets,
    nextOffset: hasMore ? rangeStart + pageSize : null,
  }
}

const ASSETS_SEARCH_FETCH_LIMIT = 100

type SearchUserAssetsInput = {
  userId: string
  search: string
}

function assetMatchesSearch(asset: ParsedAsset, searchLower: string): boolean {
  const address = asset.address
  const addressParts = address
    ? [
        createAddressTitle(address, false),
        address.street,
        address.city,
        address.postal_code,
        address.municipality,
        address.region,
      ].filter(Boolean)
    : []
  const searchableText = `${asset.name ?? ''} ${addressParts.join(' ')}`.toLowerCase()

  return searchableText.includes(searchLower)
}

export async function searchUserAssets({
  userId,
  search,
}: SearchUserAssetsInput): Promise<ParsedAsset[]> {
  if (!userId) {
    return []
  }

  const trimmedSearch = search.trim().replaceAll(',', ' ').replaceAll('%', '')

  if (trimmedSearch.length === 0) {
    return []
  }

  const searchLower = trimmedSearch.toLowerCase()

  const { data, error } = await supabase
    .from('assets')
    .select(ASSETS_SELECT_WITH_FEEDBACK)
    .is('deleted_at', null)
    .eq('users_assets.user_id', userId)
    .is('asset_prices.deleted_at', null)
    .order('created_at', { ascending: false, referencedTable: 'users_assets' })
    .limit(ASSETS_SEARCH_FETCH_LIMIT)

  if (error) {
    console.error(error)
    return []
  }

  const rawItems = data ?? []
  const assets = rawItems.map((rawItem) => parseAsset(rawItem))

  return assets.filter((asset) => assetMatchesSearch(asset, searchLower))
}
